import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import type { ScenarioProjection } from '../domain/types';
import { ScenarioCriticalUnknowns } from './ScenarioCriticalUnknowns';
import { ScenarioEntryList } from './ScenarioEntryList';
import { ScenarioLocalSopReference } from './ScenarioLocalSopReference';
import { ScenarioMemberStatus } from './ScenarioMemberStatus';
import { SourceDrawer, type DrawerContent } from './ScenarioSourceDrawer';
import { ScenarioSourceInspector } from './ScenarioSourceInspector';
import { ScenarioTemplateSelector } from './ScenarioTemplateSelector';

/**
 * The maintenance and source view — folded shut by default, and never business content.
 *
 * WHY IT EXISTS
 *   The redesign removed internal object codes, version pins, English status enums and the
 *   raw source fields from what a rescue reader sees. It removed their PRESENTATION, not the
 *   records: the source-bound projection, the exact pins, the explicit Unknown declarations,
 *   the mission-template read chain and the local-SOP reference all stay reachable here, for
 *   the people who maintain the content and for anyone auditing what the business page was
 *   derived from.
 *
 *   Nothing in here is a business explanation of a firefighting problem, and nothing in here
 *   authorizes anything.
 */
export function ScenarioMaintenancePanel({
  data,
  developmentLoopbackOrigin,
}: {
  readonly data: ScenarioProjection;
  readonly developmentLoopbackOrigin?: string;
}) {
  const [drawer, setDrawer] = useState<DrawerContent | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const open = (content: DrawerContent, event?: MouseEvent<HTMLButtonElement>) => {
    if (event) opener.current = event.currentTarget;
    setDrawer(content);
  };
  return (
    <details className="scc-source scc-maintenance">
      <summary>维护与来源视图（非业务内容）</summary>
      <p>
        以下内容用于内容维护与来源追溯。它们不是消防救援的业务说明，也不构成任何授权。
        普通阅读不需要打开本区域。
      </p>
      <div className="scc-maintenance-actions">
        <button onClick={(event) => open({ kind: 'source' }, event)}>版本、来源与适用边界 →</button>
        <button onClick={(event) => open({ kind: 'unknowns' }, event)}>Critical Unknowns →</button>
      </div>
      <ScenarioMemberStatus provenance={data.provenance} />
      <ScenarioCriticalUnknowns data={data} />
      {(
        [
          ['risks', '风险源条目'],
          ['information', '信息需求源条目'],
          ['capability', '能力需求源条目'],
          ['machine', '机器优先建议源条目'],
          ['products', '技术产品关联源条目'],
        ] as const
      ).map(([module, title]) => (
        <ScenarioEntryList key={module} entries={data.entries[module]} title={title} />
      ))}
      <ScenarioTemplateSelector
        data={data}
        {...(developmentLoopbackOrigin ? { developmentLoopbackOrigin } : {})}
        onSource={() => open({ kind: 'source' })}
        onSop={() => undefined}
      />
      <ScenarioLocalSopReference onSource={() => open({ kind: 'source' })} />
      <ScenarioSourceInspector data={data} />
      {drawer && (
        <SourceDrawer
          data={data}
          content={drawer}
          issue="HRF-HZ-02"
          restoreFocusTo={opener.current}
          onClose={() => setDrawer(null)}
        />
      )}
    </details>
  );
}
